import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { DatePicker, Select, Table, TableColumnsType } from 'antd';

import moment from 'moment';

import ErrorResponse from '../../../components/ErrorResponse';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Button from '../../../components/Button';
import config from '../../../lib/config';
import useAxios from '../../../hooks/useAxios';
import { getRetiredStudentsTodo } from '../../../api/refunds.api';
import { IGetRetiredStudentsTodo, IRetiredStudentItemTodo } from '../../../models/refunds.model';
import Loader from '../../../components/Loader';
import Pagination from '../../../components/Pagination';
import QueryString from 'qs';
import _ from 'lodash';
import { getCommonCodeLabel } from '../../../lib/store';
import SelectCampus from '../../../components/SelectCampus';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootReducer';
import DownloadButton from '../../../components/DownloadButton';
import exportTable from '../../../lib/exportTable';
const { Option } = Select;

const PaymentRetire = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { data: res, isLoading, error, execute } = useAxios<IGetRetiredStudentsTodo>();

  const [data, setData] = useState<IRetiredStudentItemTodo[]>([]);
  const [status, setStatus] = useState<string>();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [studentName, setStudentName] = useState('');
  const [page, setPage] = useState(1);
  const [param, setParam] = useState<any>({});

  useEffect(() => {
    if (currentCampus) {
      execute(getRetiredStudentsTodo(currentCampus));
    }
  }, [currentCampus]);

  useEffect(() => {
    if (res && !isLoading) {
      // query to state
      const _param: any = {};

      if (query.page) {
        setPage(Number(query.page));
        _param.page = Number(query.page);
      }
      if (query.status) {
        setStatus(String(query.status));
        _param.status = String(query.status);
      }
      if (query.startDate) {
        setStartDate(String(query.startDate));
        _param.startDate = String(query.startDate);
      }
      if (query.endDate) {
        setEndDate(String(query.endDate));
        _param.endDate = String(query.endDate);
      }
      if (query.studentName) {
        setStudentName(String(query.studentName));
        _param.studentName = String(query.studentName);
      }

      setParam(_param);

      // data filtering
      let _data: IRetiredStudentItemTodo[] = _.cloneDeep(res.data);

      if (query.status) {
        _data = _data.filter(
          (item: IRetiredStudentItemTodo) =>
            item.calculateStatus === String(query.status),
        );
      }
      if (query.startDate && query.endDate) {
        _data = _data.filter(
          (item: IRetiredStudentItemTodo) =>
            moment(item.firstRegDttm).isAfter(String(query.startDate)) &&
            moment(item.firstRegDttm).isBefore(String(query.endDate)),
        );
      }
      if (query.studentName) {
        _data = _data.filter((item: IRetiredStudentItemTodo) =>
          item.memberName
            .toLowerCase()
            .includes(String(query.studentName).toLowerCase()),
        );
      }

      setData(_data);
    }
  }, [res, isLoading, location]);

  const onSearch = () => {
    const _param: any = { page: 1 };

    if (status) {
      _param.status = status;
    }
    if (startDate && endDate) {
      _param.startDate = startDate;
      _param.endDate = endDate;
    }
    if (studentName) {
      _param.studentName = studentName;
    }

    setParam(_param);
    history.push(`/payment/retire?${QueryString.stringify(_param)}`);
  };

  const onExportTable = () => {
    const _data: any = _.cloneDeep(data);
    _data.forEach((item: any) => {
      item.firstRegDttm = moment(item.firstRegDttm).format('YYYY.MM.DD');
      item.calculateStatus = getCommonCodeLabel('PA0010', item.calculateStatus, 'ko');
    });
    const currentDate = moment(new Date());
    exportTable(columns, _data, `${t('payment.retire.4')}_(${currentDate.format('YYYYMMDD')})`, t('payment.retire.4'), [10, 20, 15, 15, 15, 10]);
  }

  const columns: TableColumnsType<any> = [
    {
      title: t('payment.retire.column1'),
      dataIndex: 'memberName',
      align: 'center',
      sorter: (a, b) => a.memberName?.localeCompare(b.memberName),
    },
    {
      title: t('common.menu.class'),
      dataIndex: 'className',
      align: 'center',
      sorter: (a, b) => a.className?.localeCompare(b.className),
    },
    {
      title: t('payment.retire.column2'),
      dataIndex: 'firstRegDttm',
      align: 'center',
      sorter: (a, b) => a.firstRegDttm?.localeCompare(b.firstRegDttm),
      render: value => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('payment.retire.column3'),
      dataIndex: 'totalRefundAmount',
      align: 'center',
      sorter: (a, b) => a.totalRefundAmount - b.totalRefundAmount,
      render: value => value?.toLocaleString(),
    },
    {
      title: t('payment.retire.column4'),
      dataIndex: 'totalPaymentAmount',
      align: 'center',
      sorter: (a, b) => a.totalPaymentAmount - b.totalPaymentAmount,
      render: value => value?.toLocaleString(),
    },
    {
      title: t('common.column.status'),
      dataIndex: 'calculateStatus',
      align: 'center',
      sorter: (a, b) => a.calculateStatus?.localeCompare(b.calculateStatus),
      render: value => getCommonCodeLabel('PA0010', value, i18n.language),
    },
  ];

  return (
    <>
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.menu.campus')}</label>
            <div>
              <SelectCampus />
            </div>
          </li>
          <li>
            <label>{t('common.column.status')}</label>
            <div>
              <Select
                placeholder={t('common.column.status')}
                value={status}
                onChange={(value) => setStatus(value)}
              >
                <Option value="">{t('common.text.all')}</Option>
                <Option value="FI">{t('payment.retire.option1')}</Option>
                <Option value="WA">{t('payment.retire.option2')}</Option>
              </Select>
            </div>
          </li>
          <li>
            <label>{t('sms.list.column1')}</label>
            <div className="date-picker__area">
              <DatePicker
                value={startDate ? moment(startDate) : undefined}
                onChange={(e) => {
                  if (e === null) setStartDate('');
                  else setStartDate(moment(e).format('YYYYMMDD'));
                }}
              />
              ~
              <DatePicker
                value={endDate ? moment(endDate) : undefined}
                onChange={(e) => {
                  if (e === null) setEndDate('');
                  else setEndDate(moment(e).format('YYYYMMDD'));
                }}
              />
            </div>
          </li>
          <li>
            <label>{t('payment.coupon.placeholder2')}</label>
            <input
              type="text"
              className="form-control w-150p"
              placeholder={t('payment.coupon.placeholder2')}
              value={studentName}
              onChange={(e) => setStudentName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
            />
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={onSearch}
                text={t('common.button.search')}
              />
          </div>
          </li>
        </ul>
      </div>
    </div>
    <div className="content scrollbar">
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <SimpleHeader
        iconFile="icon-payment-big.svg"
        parentMenu={t('common.menu.payment')}
        currentMenu={t('payment.retire.title1')}
        onlyTitle={true}
      />

      <div className="col-frame">
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={data || undefined}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  history.push(
                    `/payment/retire/${record.calculateCode}/detail${location.search}`,
                  );
                },
              };
            }}
            rowClassName="pointer"
          />
        </div>
        <div className="comm-bx__between">
          <DownloadButton onClick={onExportTable} />
          <div className="common-paging center">
            <Pagination
              count={data?.length}
              page={page}
              param={param}
              path="/payment/retire"
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default PaymentRetire;
